import { useEffect, useState } from "react";
import { api, qs } from "@/lib/api";
import { Card, KpiCard, SectionTitle, NdBadge } from "@/components/Kpi";
import { ComparisonChart, PieBreakdown } from "@/components/Charts";
import { FilterBar, useFiltersMeta, prodottoLabel, MESE_CORRENTE } from "@/components/Filters";
import { dateIt, eur, eurShort, MESI, num, statoClass } from "@/lib/format";
import { NuovaPraticaModal } from "@/components/NuovaPraticaModal";
import { PREMI_KEYS } from "@/components/PremiChart";
import { toast } from "sonner";
import GrigliaDashboard from "@/components/GrigliaDashboard";
import { useAuth } from "@/context/AuthContext";
import { TempoRisposta } from "@/components/TempoRisposta";

// colori fissi per operatrice: non cambiano con la classifica
const COLORI_GESTIONE = { ELENA: "#2563EB", MARTINA: "#0EA5E9", GAIA: "#10B981", ELEONORA: "#F59E0B",
  "DEALER NO AP": "#94A3B8", ROSY: "#EF4444" };
const ANNO_DASHBOARD = 2026;

/** Pratiche caricate giorno per giorno, con dettaglio del giorno selezionato. */
export default function PraticheGiornaliere() {
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: ANNO_DASHBOARD, mese: MESE_CORRENTE });
  const [d, setD] = useState(null);
  const [giorno, setGiorno] = useState(null);
  const [dettaglio, setDettaglio] = useState(null);
  // il pannello del giorno si apre subito sotto il grafico/tabella da cui è stato scelto
  const [dettaglioDopo, setDettaglioDopo] = useState("chart-giornaliero-caricate");
  const [tipoGiorno, setTipoGiorno] = useState("caricato");
  const apriGiorno = (data, da, tipo = "caricato") => { setDettaglioDopo(da); setTipoGiorno(tipo); setGiorno(data); };
  const [dealers, setDealers] = useState([]);
  const [nuovaOpen, setNuovaOpen] = useState(false);
  const [gestioneGiorno, setGestioneGiorno] = useState(null);
  const intervallo = Boolean(filters.dal || filters.al);
  const { puo, user } = useAuth();

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ ...filters, dealer: "" })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.mese, filters.macro, filters.prodotto, filters.commerciale]);

  useEffect(() => {
    setGiorno(null);
    setDettaglio(null);
    api.get(`/dashboard/giornaliero?${qs(filters)}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [filters]);

  useEffect(() => {
    if (!giorno) return;
    setDettaglio(null);
    const chiaveGiorno = tipoGiorno === "liquidato" ? { giorno_liquidazione: giorno } : { giorno };
    api.get(`/pratiche?${qs({ ...filters, mese: "", ...chiaveGiorno, dataset: "giornaliere", page_size: 200, sort: "external_id", direction: 1 })}`)
      .then((r) => setDettaglio(r.data)).catch(() => setDettaglio({ rows: [], total: 0 }));
  }, [giorno, filters]);

  // la torta "gestione caricamento" segue il giorno cliccato sull'istogramma
  useEffect(() => {
    if (!giorno) { setGestioneGiorno(null); return; }
    api.get(`/dashboard/gestione-caricamento?${qs({ ...filters, mese: "", dal: "", al: "", giorno })}`)
      .then((r) => setGestioneGiorno(r.data.gestione_caricamento)).catch(() => setGestioneGiorno(null));
  }, [giorno, filters]);

  const giorni = d?.giorni || [];
  const annoIntero = !filters.mese && !intervallo;
  const serieIstogramma = annoIntero
    ? (d?.serie_mensile || []).map((r) => ({
      label: r.label, mese: r.mese, caricate: r.pratiche_2026, importo_caricato: r.caricato_2026 }))
    : giorni;
  const serieLiquidate = annoIntero
    ? (d?.serie_mensile || []).map((r) => ({
      label: r.label, mese: r.mese, liquidate: r.liquidate_2026, importo_liquidato: r.liquidato_2026 }))
    : (d?.liquidate_giorni || []);
  const t = d?.totali || {};
  // pratiche di oggi: dal dettaglio giornaliero del mese in corso
  const oggiIso = new Date().toLocaleDateString("sv-SE");
  const praticheOggi = (d?.giorni || []).find((g) => g.data === oggiIso)?.caricate ?? 0;
  const rigaGiorno = giorni.find((g) => g.data === giorno);
  const rigaGiornoLiq = (d?.liquidate_giorni || []).find((g) => g.data === giorno);
  // progressivo da inizio anno su pratiche caricate AUTO
  const ytd = (d?.assicurativi || []).reduce((a, r) => ({
    auto: a.auto + (r.pratiche_auto || 0),
    cpi: a.cpi + (r.n_cpi || 0),
    cvt: a.cvt + (r.n_cvt || 0),
    ggn: a.ggn + (r.n_ggn || 0),
    autoGgn: a.autoGgn + (r.pratiche_auto_ggn || 0),
  }), { auto: 0, cpi: 0, cvt: 0, ggn: 0, autoGgn: 0 });
  const ytdDealer = (d?.serie_mensile || []).reduce((a, r) => ({
    caricate: a.caricate + (r.pratiche_2026 || 0),
    dealer: a.dealer + (r.dal_dealer_2026 || 0),
  }), { caricate: 0, dealer: 0 });
  const ytdFirma = (d?.firma_digitale || []).reduce((a, r) => ({
    valorizzate: a.valorizzate + (r.valorizzate || 0),
    con_firma: a.con_firma + (r.con_firma || 0),
  }), { valorizzate: 0, con_firma: 0 });
  const ytdPrivacy = (d?.privacy || []).reduce((a, r) => ({
    liquidate: a.liquidate + (r.liquidate || 0),
    con_privacy: a.con_privacy + (r.con_privacy || 0),
  }), { liquidate: 0, con_privacy: 0 });
  const ytdPremi = (d?.premi || []).reduce((a, r) => ({
    cpi: a.cpi + (r.importo_cpi || 0),
    cvt: a.cvt + (r.importo_cvt || 0),
    nfk: a.nfk + (r.importo_nfk || 0),
    ggn: a.ggn + (r.importo_ggn || 0),
  }), { cpi: 0, cvt: 0, nfk: 0, ggn: 0 });

  const gestione = (giorno && gestioneGiorno) || d?.gestione_caricamento || [];
  const periodoLabel = giorno ? dateIt(giorno)
    : intervallo ? `${filters.dal ? dateIt(filters.dal) : "inizio"} → ${filters.al ? dateIt(filters.al) : "oggi"}`
      : filters.mese ? `${MESI[filters.mese - 1]} ${ANNO_DASHBOARD}` : `tutto l'anno ${ANNO_DASHBOARD}`;
  const gestionePersone = gestione.map((g) => {
    const name = g.gestione === "DEALER NO AP" ? "Dealer no AP" : g.gestione;
    const fisso = COLORI_GESTIONE[name.trim().toUpperCase()];
    return { name, value: g.pratiche, ...(fisso ? { color: fisso } : g.persona ? {} : { color: "#94A3B8" }) };
  });
  const totGestione = gestionePersone.reduce((a, g) => a + g.value, 0);

  const pannelloGiorno = giorno ? (
        <Card className="border-2 border-sky-600 bg-gradient-to-b from-sky-50/70 to-white"
          data-testid="giorno-dettaglio">
          <div className="p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="uppercase text-xs tracking-[0.18em] font-bold text-sky-700">
                Dettaglio del {dateIt(giorno)}
              </div>
              <div className="mt-2 font-mono text-sm text-slate-600">
                {tipoGiorno === "liquidato"
                  ? <>{num(rigaGiornoLiq?.liquidate)} liquidate · {eurShort(rigaGiornoLiq?.importo_liquidato)}</>
                  : <>{num(rigaGiorno?.caricate)} caricate · {eurShort(rigaGiorno?.importo_caricato)} ·{" "}
                    {num(rigaGiorno?.caricate_dal_dealer)} caricate dal dealer</>}
              </div>
            </div>
            <button type="button" data-testid="giorno-chiudi" onClick={() => setGiorno(null)}
              className="text-sm px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-600
                hover:text-slate-900 hover:border-slate-300 transition-colors duration-200">
              Chiudi
            </button>
          </div>
          <div className="overflow-x-auto">
            {!dettaglio ? (
              <div className="px-6 pb-6 text-sm text-slate-500">Caricamento…</div>
            ) : dettaglio.rows.length === 0 ? (
              <div className="px-6 pb-6"><NdBadge label="nessuna pratica in questo giorno" /></div>
            ) : (
              <table className="w-full text-sm bg-white" data-testid="giorno-pratiche-table">
                <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <th className="text-left px-6 py-3">N° pratica</th>
                  <th className="text-left px-6 py-3">Cliente</th>
                  <th className="text-left px-6 py-3">Dealer</th>
                  <th className="text-left px-6 py-3">Commerciale</th>
                  <th className="text-left px-6 py-3">Prodotto</th>
                  <th className="text-left px-6 py-3">Stato</th>
                  <th className="text-right px-6 py-3">Importo</th>
                </tr></thead>
                <tbody>
                  {dettaglio.rows.map((p) => (
                    <tr key={p.external_id} className="border-t border-slate-100"
                      data-testid={`giorno-pratica-${p.external_id}`}>
                      <td className="px-6 py-2 font-mono">{p.external_id}</td>
                      <td className="px-6 py-2">{p.cliente || "—"}</td>
                      <td className="px-6 py-2">{p.dealer_nome || "—"}</td>
                      <td className="px-6 py-2 text-slate-500">{p.commerciale_nome_raw || "—"}</td>
                      <td className="px-6 py-2 text-slate-500">{prodottoLabel(meta, p.prodotto)}</td>
                      <td className="px-6 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-semibold ${statoClass(p.stato_norm)}`}
                          data-testid={`giorno-pratica-stato-${p.external_id}`}>
                          {(p.stato || p.stato_norm || "—").toString().replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-6 py-2 text-right font-mono">{p.importo_finanziato ? eur(p.importo_finanziato) : "n/d"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
  ) : null;

  return (
    <div data-testid="giornaliero-page">
      <SectionTitle sub="Pratiche caricate giorno per giorno. Clicca una barra o una riga per vedere il dettaglio del giorno."
        right={
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <a href={`/api/report/premi-dealer.xlsx?${qs({ anno: filters.anno, commerciale: filters.commerciale, dealer: filters.dealer })}`}
              data-testid="btn-premi-dealer-xlsx"
              className="px-4 py-2 rounded-md border border-[#0018A8] text-[#0018A8] text-sm hover:bg-blue-50
                transition-colors duration-200">
              Excel premi per dealer
            </a>
            <a href={`/api/report/giornaliero.pdf?${qs(filters)}`} target="_blank" rel="noreferrer"
              data-testid="btn-report-giornaliero-pdf"
              className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480]
                transition-colors duration-200">
              Scarica report PDF
            </a>
          </div>
        }>
        Pratiche caricate giornalmente
      </SectionTitle>
      <FilterBar meta={meta} value={filters} onChange={setFilters}
        fields={["mese", "intervallo", "commerciale", "macro", "prodotto", "dealer", "gestione"]} dealers={dealers}
        extra={
          <button type="button" data-testid="filtri-reset"
            onClick={() => setFilters({ anno: ANNO_DASHBOARD, mese: MESE_CORRENTE })}
            className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white font-semibold
              hover:bg-sky-700 transition-colors duration-200">
            Azzera filtri
          </button>
        } />

      <div className={`grid grid-cols-1 ${puo("tempo_risposta") ? "xl:grid-cols-2" : ""} gap-4 mb-4 items-start`}
        data-testid="giornaliero-riga-compatta">
        {puo("tempo_risposta") && <TempoRisposta filters={filters} puoScaricare={user?.role === "MASTER"} />}
        <PieBreakdown testId="chart-gestione-persone" valueKind="num" height={120} legendSide senzaEtichette
          title={`Gestione caricamento · ${periodoLabel} · ${num(totGestione)} pratiche`}
          data={gestionePersone} emptyNote="nessuna gestione indicata nel file pratiche" />
      </div>

      <GrigliaDashboard pagina="giornaliero" filtri={filters} className="mb-6" blocchi={[
        ...(giorno && !dettaglioDopo.includes("liquidat") ? [{ id: "giorno-dettaglio-blocco", label: "Dettaglio del giorno", span: 4, dopo: dettaglioDopo, node: pannelloGiorno }] : []),
        { id: "g-kpi-oggi", label: "Pratiche odierne", span: 1,
          node: <KpiCard testId="g-kpi-oggi" label="Pratiche odierne" value={praticheOggi}
            sub={`aggiornato al ${dateIt(oggiIso)}`} accent="sky" /> },
        { id: "g-kpi-caricate", label: "Pratiche caricate", span: 1,
          node: <KpiCard testId="g-kpi-caricate" label="Pratiche caricate" value={t.pratiche_caricate} accent="sky" /> },
        { id: "g-kpi-importo", label: "Importo caricato", span: 1,
          node: <KpiCard testId="g-kpi-importo" label="Importo caricato" value={t.importo_caricato} kind="eurShort" accent="sky" /> },
        { id: "g-kpi-media", label: "Media giornaliera", span: 1,
          node: <KpiCard testId="g-kpi-media" label="Media giornaliera" value={d?.giorni_attivi ? Math.round(t.pratiche_caricate / d.giorni_attivi) : null} accent="skyLight" /> },
        { id: "chart-giornaliero-caricate", label: "Pratiche caricate per giorno/mese", span: 4,
          node: <ComparisonChart testId="chart-giornaliero-caricate" height={340} labelFormat="int"
            title={annoIntero ? `Pratiche caricate per mese · ${ANNO_DASHBOARD}` : `Pratiche caricate per giorno${intervallo ? ` · ${periodoLabel}` : ""}`}
            data={serieIstogramma}
            onBarClick={(p) => (annoIntero ? setFilters((f) => ({ ...f, mese: p.mese })) : apriGiorno(p.data, "chart-giornaliero-caricate"))}
            keys={[{ key: "caricate", name: "Caricate", color: "#0284C7" }]}
            emptyNote="nessuna pratica nel periodo selezionato" /> },
        { id: "chart-giornaliero-importo", label: "Importo caricato per giorno/mese", span: 4,
          node: <ComparisonChart testId="chart-giornaliero-importo" height={340}
            title={annoIntero ? `Importo caricato per mese · ${ANNO_DASHBOARD}` : `Importo caricato per giorno${intervallo ? ` · ${periodoLabel}` : ""}`}
            data={serieIstogramma}
            onBarClick={(p) => (annoIntero ? setFilters((f) => ({ ...f, mese: p.mese })) : apriGiorno(p.data, "chart-giornaliero-importo"))}
            keys={[{ key: "importo_caricato", name: "Caricato", color: "#7DD3FC" }]}
            emptyNote="nessun importo nel periodo selezionato" /> },
        { id: "chart-ingressi-giornalieri", label: "Ingresso pratiche", span: 4,
          node: <ComparisonChart testId="chart-ingressi-giornalieri" height={300} type="line" labelFormat="int"
            title={annoIntero
              ? `Ingresso pratiche per mese · da DATA INGRESSO · ${ANNO_DASHBOARD}`
              : "Ingresso pratiche per giorno · da DATA INGRESSO"}
            data={d?.ingressi || []}
            keys={[{ key: "ingressi", name: "Ingressi", color: "#0284C7" }]}
            emptyNote="nessun ingresso nel periodo selezionato" /> },
      ]} />

      <div className="mt-6 space-y-6">
        <ComparisonChart testId="chart-dealer-mensile" height={320} labelFormat="int"
          title={`Pratiche caricate dal dealer per mese · ${ANNO_DASHBOARD}`}
          data={d?.serie_mensile || []}
          keys={[{ key: "dal_dealer_2026", name: "Caricate dal dealer", color: "#4ADE80" }]}
          emptyNote="dato non disponibile" />
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-6 items-stretch">
          <ComparisonChart testId="chart-dealer-mensile-perc" height={300} labelFormat="perc"
            title={`% pratiche caricate dal dealer sul totale caricate · ${ANNO_DASHBOARD}`}
            data={d?.serie_mensile || []}
            keys={[{ key: "perc_dal_dealer_2026", name: "% dal dealer", color: "#4ADE80" }]}
            emptyNote="dato non disponibile" />
          <Card className="p-6 flex flex-col justify-center border-2 border-emerald-500 bg-gradient-to-b from-emerald-50/70 to-white"
            data-testid="ytd-dealer">
            <div className="uppercase text-xs tracking-[0.18em] font-bold text-emerald-700">
              Progressivo anno · % dal dealer
            </div>
            <div className="mt-3 font-mono text-3xl font-semibold text-emerald-600">
              {ytdDealer.caricate ? `${num(ytdDealer.dealer / ytdDealer.caricate * 100, 2)}%` : "n/d"}
            </div>
            <div className="mt-2 text-sm text-slate-500 font-mono">
              {num(ytdDealer.dealer)} su {num(ytdDealer.caricate)} pratiche caricate
            </div>
          </Card>
        </div>
        <ComparisonChart testId="chart-liquidate-giorno" height={300} labelFormat="int"
            title={annoIntero ? `Pratiche liquidate per mese · ${ANNO_DASHBOARD}` : "Pratiche liquidate per giorno"}
            data={serieLiquidate}
            onBarClick={(p) => (annoIntero ? setFilters((f) => ({ ...f, mese: p.mese })) : apriGiorno(p.data, "chart-liquidate-giorno", "liquidato"))}
            keys={[{ key: "liquidate", name: "Liquidate", color: "#16A34A" }]}
            emptyNote="nessuna liquidazione nel periodo selezionato" />
        {giorno && dettaglioDopo === "chart-liquidate-giorno" && pannelloGiorno}
          <ComparisonChart testId="chart-liquidato-giorno" height={300}
            title={annoIntero ? `Importo liquidato per mese · ${ANNO_DASHBOARD}` : "Importo liquidato per giorno"}
            data={serieLiquidate}
            onBarClick={(p) => (annoIntero ? setFilters((f) => ({ ...f, mese: p.mese })) : apriGiorno(p.data, "chart-liquidato-giorno", "liquidato"))}
            keys={[{ key: "importo_liquidato", name: "Liquidato", color: "#86EFAC" }]}
            emptyNote="nessun importo liquidato nel periodo selezionato" />
        {giorno && dettaglioDopo === "chart-liquidato-giorno" && pannelloGiorno}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-6 items-stretch">
          <ComparisonChart testId="chart-cvt-perc" height={300} labelFormat="perc" type="line"
            title={`% CVT su pratiche caricate AUTO · ${ANNO_DASHBOARD}`} data={d?.assicurativi || []}
            keys={[{ key: "perc_cvt", name: "% CVT", color: "#0284C7" }]}
            emptyNote="dato non disponibile" />
          <Card className="p-6 flex flex-col justify-center border-2 border-red-600 bg-gradient-to-b from-red-50/70 to-white"
            data-testid="ytd-cvt">
            <div className="uppercase text-xs tracking-[0.18em] font-bold text-red-700">
              Progressivo anno · % CVT
            </div>
            <div className="mt-3 font-mono text-3xl font-semibold text-red-600">
              {ytd.auto ? `${num(ytd.cvt / ytd.auto * 100, 2)}%` : "n/d"}
            </div>
            <div className="mt-2 text-sm text-slate-500 font-mono">
              {num(ytd.cvt)} CVT su {num(ytd.auto)} pratiche AUTO
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-6 items-stretch">
          <ComparisonChart testId="chart-cpi-perc" height={300} labelFormat="perc" type="line"
            title={`% CPI su pratiche caricate AUTO · ${ANNO_DASHBOARD}`} data={d?.assicurativi || []}
            keys={[{ key: "perc_cpi", name: "% CPI", color: "#F59E0B" }]}
            emptyNote="dato non disponibile" />
          <Card className="p-6 flex flex-col justify-center border-2 border-orange-500 bg-gradient-to-b from-orange-50 to-white"
            data-testid="ytd-cpi">
            <div className="uppercase text-xs tracking-[0.18em] font-bold text-amber-600">
              Progressivo anno · % CPI
            </div>
            <div className="mt-3 font-mono text-3xl font-semibold text-amber-600">
              {ytd.auto ? `${num(ytd.cpi / ytd.auto * 100, 2)}%` : "n/d"}
            </div>
            <div className="mt-2 text-sm text-slate-500 font-mono">
              {num(ytd.cpi)} CPI su {num(ytd.auto)} pratiche AUTO
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-6 items-stretch">
          <ComparisonChart testId="chart-ggn-perc" height={300} labelFormat="perc" type="line"
            title={`% GGN su pratiche caricate AUTO · ${ANNO_DASHBOARD}`} data={d?.assicurativi || []}
            keys={[{ key: "perc_ggn", name: "% GGN", color: "#7C3AED" }]}
            emptyNote="nessuna pratica con GGN nel periodo" />
          <Card className="p-6 flex flex-col justify-center border-2 border-violet-600 bg-gradient-to-b from-violet-50 to-white"
            data-testid="ytd-ggn">
            <div className="uppercase text-xs tracking-[0.18em] font-bold text-violet-700">
              Progressivo anno · % GGN
            </div>
            <div className="mt-3 font-mono text-3xl font-semibold text-violet-600">
              {ytd.autoGgn ? `${num(ytd.ggn / ytd.autoGgn * 100, 2)}%` : "n/d"}
            </div>
            <div className="mt-2 text-sm text-slate-500 font-mono">
              {num(ytd.ggn)} GGN su {num(ytd.autoGgn)} pratiche AUTO da settembre
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-6 items-stretch">
          <ComparisonChart testId="chart-firma-digitale" height={300} labelFormat="perc"
            title={`% firma digitale sulle pratiche caricate · ${ANNO_DASHBOARD}`}
            data={d?.firma_digitale || []}
            keys={[{ key: "perc_firma", name: "% firma digitale", color: "#6366F1" }]}
            emptyNote="dato non disponibile" />
          <Card className="p-6 flex flex-col justify-center border-2 bd-caricato soft-caricato"
            data-testid="ytd-firma">
            <div className="uppercase text-xs tracking-[0.18em] font-bold txt-caricato">
              Progressivo anno · % firma digitale
            </div>
            <div className="mt-3 font-mono text-3xl font-semibold txt-caricato">
              {ytdFirma.valorizzate ? `${num(ytdFirma.con_firma / ytdFirma.valorizzate * 100, 2)}%` : "n/d"}
            </div>
            <div className="mt-2 text-sm text-slate-500 font-mono">
              {num(ytdFirma.con_firma)} su {num(ytdFirma.valorizzate)} pratiche valorizzate
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-6 items-stretch">
          <ComparisonChart testId="chart-privacy" height={300} labelFormat="perc"
            title={`% privacy sulle pratiche liquidate · ${ANNO_DASHBOARD}`}
            data={d?.privacy || []}
            keys={[{ key: "perc_privacy", name: "% privacy", color: "#14B8A6" }]}
            emptyNote="dato non disponibile" />
          <Card className="p-6 flex flex-col justify-center border-2 bd-liquidato soft-liquidato"
            data-testid="ytd-privacy">
            <div className="uppercase text-xs tracking-[0.18em] font-bold text-teal-700">
              Progressivo anno · % privacy
            </div>
            <div className="mt-3 font-mono text-3xl font-semibold txt-liquidato">
              {ytdPrivacy.liquidate ? `${num(ytdPrivacy.con_privacy / ytdPrivacy.liquidate * 100, 2)}%` : "n/d"}
            </div>
            <div className="mt-2 text-sm text-slate-500 font-mono">
              {num(ytdPrivacy.con_privacy)} su {num(ytdPrivacy.liquidate)} pratiche liquidate
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-6 items-stretch">
          <ComparisonChart testId="chart-premi" height={320}
            title={`Premi assicurativi liquidati per mese · ${ANNO_DASHBOARD}`}
            data={d?.premi || []}
            keys={PREMI_KEYS}
            emptyNote="dato non disponibile" />
          <Card className="p-6 flex flex-col justify-center border-2 border-slate-400 bg-gradient-to-b from-slate-50 to-white"
            data-testid="ytd-premi">
            <div className="uppercase text-xs tracking-[0.18em] font-bold text-slate-600">
              Premi liquidati
            </div>
            <div className="mt-4 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">CVT</div>
                <div className="font-mono text-xl font-semibold text-red-600" data-testid="ytd-premi-cvt">{eur(ytdPremi.cvt)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">CPI</div>
                <div className="font-mono text-xl font-semibold text-amber-600" data-testid="ytd-premi-cpi">{eur(ytdPremi.cpi)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">NFK</div>
                <div className="font-mono text-xl font-semibold text-emerald-600" data-testid="ytd-premi-nfk">{eur(ytdPremi.nfk)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">GGN</div>
                <div className="font-mono text-xl font-semibold text-violet-600" data-testid="ytd-premi-ggn">{eur(ytdPremi.ggn)}</div>
              </div>
              <div className="border-t border-slate-200 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Totale</div>
                <div className="font-mono text-2xl font-semibold text-slate-900" data-testid="ytd-premi-totale">{eur(ytdPremi.cpi + ytdPremi.cvt + ytdPremi.nfk + ytdPremi.ggn)}</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {puo("gestione_pratiche") && (
      <button type="button" onClick={() => setNuovaOpen(true)} data-testid="nuova-pratica-btn"
        className="fixed bottom-6 right-6 z-[1100] inline-flex items-center gap-2 px-5 py-3 rounded-full
          bg-sky-600 text-white text-sm font-semibold shadow-lg shadow-sky-600/30
          hover:bg-sky-700 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
        GESTISCI PRATICA
      </button>
      )}

      <NuovaPraticaModal open={nuovaOpen} meta={meta} onClose={() => setNuovaOpen(false)}
        onSaved={async (dati, modo) => {
          if (modo === "eliminata") {
            setNuovaOpen(false);
            setFilters((f) => ({ ...f }));
            return;
          }
          const salva = (body) => (modo === "recupera"
            ? api.put(`/pratiche/${encodeURIComponent(dati.numero_originale || dati.nr_pratica)}`, body)
            : api.post("/pratiche", body));
          try {
            await salva(dati);
          } catch (e) {
            const det = e?.response?.data?.detail;
            if (det?.codice === "conferma_premi") {
              const elenco = det.premi.map((p) => `${p.campo}: ${num(p.valore, 2)} €`).join("\n");
              const ok = window.confirm(`Attenzione: premio assicurativo oltre ${num(det.soglia)} €\n\n${elenco}\n\n`
                + "Controlla punti e virgole. Confermi che il dato inserito è corretto?");
              if (!ok) return;
              try { await salva({ ...dati, conferma_premi: true }); } catch (e2) {
                toast.error(e2?.response?.data?.detail || "Salvataggio non riuscito");
                return;
              }
            } else {
              toast.error(typeof det === "string" ? det : "Salvataggio non riuscito");
              return;
            }
          }
          toast.success(modo === "recupera"
            ? (dati.numero_originale && dati.numero_originale !== dati.nr_pratica
              ? `Pratica ${dati.numero_originale} rinumerata in ${dati.nr_pratica}` : `Pratica ${dati.nr_pratica} aggiornata`)
            : `Pratica ${dati.nr_pratica} inserita`);
          setNuovaOpen(false);
          setFilters((f) => ({ ...f }));
        }}
        onLiquidazione={async (numero, body, azzera) => {
          try {
            await api.put(`/pratiche/${encodeURIComponent(numero)}/liquidazione`, body);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Operazione non riuscita");
            return;
          }
          toast.success(azzera
            ? `Liquidazione della pratica ${numero} azzerata`
            : `Liquidazione della pratica ${numero} registrata${body.dealer_bloccato === "SI" ? " (dealer bloccato durante la liquidazione)" : ""}`);
          setNuovaOpen(false);
          setFilters((f) => ({ ...f }));
        }} />
    </div>
  );
}
